import React from "react";
import { BellRing } from "lucide-react";
import GlobalDetailDrawer from "modules/swiftboard/components/GlobalDetailDrawer";
import LocalDateTime from "services/date-converter";
import ActiveBadge from "../../../components/ActiveBadge";
import Chip from "../../../components/Chip";
import LifecycleBadge from "../../../components/LifecycleBadge";
import MessageList from "../../../components/MessageList";
import { TRIGGER_TYPE_LABEL } from "../../../features/builder.constants";
import { ON_SKIP_LABEL, REPEAT_UNIT_LABEL, WAIT_UNIT_LABEL } from "../features/rule.constants";
import { audienceText, offsetText, scheduleText } from "../features/rule.utils";

const counts = (map) => {
  const entries = Object.entries(map ?? {});
  return entries.length ? <span className="flex flex-wrap justify-end gap-1">{entries.map(([status, n]) => <Chip key={status}>{`${status.toLowerCase()} ${n}`}</Chip>)}</span> : "None";
};

const entryText = (entry) => {
  const config = Object.entries(entry.config ?? {}).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`);
  return `${entry.type_key}${config.length ? ` (${config.join("; ")})` : ""}${entry.channel_keys?.length ? ` on ${entry.channel_keys.join(", ")}` : ""}`;
};

function StepList({ steps }) {
  if (!steps?.length) return <span className="text-xs text-text_muted">No steps yet</span>;
  return (
    <ol className="space-y-2">
      {steps.map((step) => (
        <li key={step.id} className="rounded-lg border border-border p-2.5 text-xs">
          <p className="font-semibold text-text_primary">
            Step {step.step_no}
            {step.name ? ` · ${step.name}` : ""}
          </p>
          <p className="mt-0.5 text-text_muted">
            {step.step_no === 1 ? "Starts" : "Waits"} {step.wait_value} {(WAIT_UNIT_LABEL[step.wait_unit] ?? step.wait_unit).toLowerCase()}
            {step.max_sends ? ` · at most ${step.max_sends} sends` : ""}
            {step.digest ? " · digest" : ""}
            {step.keep_previous ? " · keeps previous recipients" : ""}
          </p>
          <p className="mt-1 text-text_secondary">To: {step.recipients.map(entryText).join(", ")}</p>
          {step.cc_recipients?.length > 0 && <p className="text-text_secondary">CC: {step.cc_recipients.map(entryText).join(", ")}</p>}
          <p className="text-text_secondary">Channels: {step.channels.map((c) => `${c.channel_key} (template #${c.template_fid})`).join(", ")}</p>
        </li>
      ))}
    </ol>
  );
}

// Read-only view of one rule (with its ladder) and its live summary, both fetched by the list page.
export default function RuleViewDrawer({ record, summary, onClose }) {
  if (!record) return null;
  const calendar = record.calendar_policy ?? {};
  const repeat = record.repeat_policy ?? {};
  const stop = record.stop_policy ?? {};
  const isDateAnchor = record.trigger?.trigger_type === "DATE_ANCHOR";
  const notices = [
    ...(summary?.has_draft_changes ? ["The saved draft differs from the live version. Activate again to publish it."] : []),
    ...(summary?.outdated?.trigger ? ["The trigger was published again since this rule was activated."] : []),
    ...(summary?.outdated?.source ? ["The trigger's source changed since this rule was activated."] : []),
    ...(summary?.outdated?.templates?.length ? [`Newer template versions: ${summary.outdated.templates.join(", ")}.`] : []),
  ];
  const lastRun = summary?.last_run;

  const sections = [
    ...(notices.length ? [{ title: "Needs attention", fields: [{ value: <MessageList tone="warning" items={notices} /> }] }] : []),
    {
      title: "Rule",
      fields: [
        { label: "Name", value: record.name },
        { label: "Code", value: <span className="font-mono">{record.code}</span> },
        { label: "Lifecycle", value: <LifecycleBadge status={record.status} version={record.published_version_no || undefined} /> },
        { label: "Trigger", value: record.trigger ? `${record.trigger.name} (${TRIGGER_TYPE_LABEL[record.trigger.trigger_type] ?? record.trigger.trigger_type})` : "—" },
        { label: "Module", value: record.module?.label ?? "—" },
        { label: "Priority", value: record.priority },
        { label: "Tags", value: record.tags?.length ? <span className="flex flex-wrap gap-1">{record.tags.map((t) => <Chip key={t}>{t}</Chip>)}</span> : "—" },
        { label: "Status", value: <ActiveBadge active={record.is_activated} /> },
        { label: "Description", value: record.description || "—" },
      ],
    },
    {
      title: "Live",
      fields: [
        { label: "Next run", value: summary?.next_run_local && record.status === "ACTIVE" ? `${summary.next_run_local} (${summary.timezone})` : "—" },
        { label: "Last run", value: lastRun ? <span>{lastRun.status.toLowerCase()} · <LocalDateTime utcTime={lastRun.started_at} /> · {lastRun.candidates} found, {lastRun.opened} opened</span> : "Not run yet" },
        { label: "Published", value: record.published_at ? <LocalDateTime utcTime={record.published_at} /> : "—" },
        { label: "Cases", value: counts(summary?.instances) },
        { label: "Deliveries today", value: counts(summary?.deliveries_today) },
      ],
    },
    {
      title: "When",
      fields: [
        { label: "Schedule", value: scheduleText(record.schedule) },
        { label: "Timezone", value: `${record.timezone || "Set on activation"}${record.timezone_basis === "SUBJECT" ? " · each user's own" : ""}` },
        ...(isDateAnchor ? [{ label: "Offsets", value: (record.offsets ?? []).map((o) => offsetText(o, record.offset_unit)).join(", ") || "—" }] : []),
        { label: "Valid", value: record.valid_from || record.valid_to ? `${record.valid_from?.slice(0, 10) ?? "any time"} to ${record.valid_to?.slice(0, 10) ?? "no end"}` : "Always" },
      ],
    },
    {
      title: "Who and policies",
      fields: [
        { label: "Audience", value: `${audienceText(record.audience, { isBroadcast: record.trigger?.trigger_type === "BROADCAST" })}${record.audience?.active_only === false ? " · incl. inactive" : ""}` },
        { label: "Calendar", value: [calendar.working_days_only && "working days only", calendar.quiet_hours && `quiet ${calendar.quiet_hours.join("-")}`, calendar.working_days_only && ON_SKIP_LABEL[calendar.on_skip]?.toLowerCase()].filter(Boolean).join(" · ") || "Any day, any time" },
        { label: "Repeat", value: repeat.mode === "EVERY" ? `Every ${repeat.every} ${(REPEAT_UNIT_LABEL[repeat.unit] ?? repeat.unit).toLowerCase()}, at most ${repeat.max_count}` : "Once per step" },
        { label: "Stop", value: [stop.on_condition_cleared !== false && "record no longer matches", stop.on_subject_inactive !== false && "user deactivated", stop.on_max_reached !== false && "last step done", stop.after_days && `after ${stop.after_days} days`].filter(Boolean).join(" · ") || "—" },
        { label: "Opt-out", value: record.allow_opt_out && record.preference_key ? `Honours ${record.preference_key}` : "Not possible" },
      ],
    },
    { title: `Ladder (${record.steps?.length ?? 0})`, fields: [{ value: <StepList steps={record.steps} /> }] },
  ];

  return <GlobalDetailDrawer open onClose={onClose} title={record.name} subtitle={record.code} icon={<BellRing size={20} />} sections={sections} />;
}
